import { useCallback, useEffect, useMemo, useState } from "react";
import { BsCheckCircle, BsTruck, BsXOctagon } from "react-icons/bs";
import { FiCalendar, FiPlus } from "react-icons/fi";
import { GoLocation } from "react-icons/go";
import { IoMdArrowDropright } from "react-icons/io";
import { PiTableBold } from "react-icons/pi";
import { useDispatch } from "react-redux";
import Breadcrumbs from "../../components/Breadcrumbs";
import CustomModal from "../../components/CustomModal";
import CustomTabs from "../../components/CustomTabs";
import Input from "../../components/input";
import NewTableComponent from "../../components/NewTableComponent";
import Notification from "../../components/Notification";
import Select from "../../components/Select";
import Stats from "../../components/Stats";
import { fetchNotificationsData } from "../../store/Slices/NotificationSlice";
import "../../styles/Access.css";
import "../../styles/Button.css";
import "../../styles/Table.css";
import "../../styles/TransportersTable.css";
import "../../styles/Trip.css";
import "../../styles/ThreePLQuotations.css";
import { loadBookings } from "./loadBooking/loadBooking.helpers";
import {
  QUOTATION_DEFAULT_LIMIT,
  QUOTATION_LIMIT_OPTIONS,
  QUOTATION_TAB,
} from "./quotation/quotation.constants";
import {
  applyQuotationFilters,
  buildLoadQuotationRows,
  buildQuotationOverview,
  formatAmount,
  generateQuotationId,
  loadQuotationRecords,
  normalize,
  paginateRows,
  safeArray,
  saveQuotationRecords,
  searchQuotationRows,
} from "./quotation/quotation.helpers";

const createTableState = () => ({
  searchText: "",
  searchFilters: {},
  currentPage: 1,
  limit: QUOTATION_DEFAULT_LIMIT,
});

const QUOTATION_TAB_META = {
  [QUOTATION_TAB.customer]: {
    key: QUOTATION_TAB.customer,
    title: "Customer Quotations",
    shortTitle: "Customer",
    createLabel: "Create Customer Quotation",
    modalTitle: "Create Customer Quotation",
    partyLabel: "Customer Name",
    partyPlaceholder: "Select Customer",
    icon: <BsCheckCircle />,
    emptyTitle: "No customer quotations yet",
    emptyDescription:
      "Create a customer quotation or add mock load booking data to populate this table.",
  },
  [QUOTATION_TAB.transporter]: {
    key: QUOTATION_TAB.transporter,
    title: "Transporter Quotations",
    shortTitle: "Transporter",
    createLabel: "Create Transporter Quotation",
    modalTitle: "Create Transporter Quotation",
    partyLabel: "Transporter Name",
    partyPlaceholder: "Select Transporter",
    icon: <BsTruck />,
    emptyTitle: "No transporter quotations yet",
    emptyDescription:
      "Create a transporter quotation or add mock load booking data to populate this table.",
  },
};

const toDateLabel = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "--";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const buildQuotationColumns = (partyHeading) => [
  {
    name: "Load ID",
    selector: (row) => row.loadId,
    cell: (row) => <span className="quotation_link">{row.loadId || "-"}</span>,
    sortable: false,
    tableColumn: true,
    filterName: "loadIdFilter",
    type: "text",
    minWidth: "120px",
  },
  {
    name: partyHeading,
    selector: (row) => row.partyName,
    cell: (row) => <span className="quotation_link">{row.partyName || "-"}</span>,
    sortable: false,
    tableColumn: true,
    filterName: "partyNameFilter",
    type: "text",
    minWidth: "220px",
  },
  {
    name: "POC Name",
    selector: (row) => row.pocName,
    cell: (row) => <span className="quotation_link">{row.pocName || "-"}</span>,
    sortable: false,
    tableColumn: true,
    filterName: "pocNameFilter",
    type: "text",
    minWidth: "160px",
  },
  {
    name: "POC Contact",
    selector: (row) => row.pocContact,
    sortable: false,
    tableColumn: true,
    filterName: "pocContactFilter",
    type: "text",
    minWidth: "170px",
  },
  {
    name: "Buying Price",
    selector: (row) => formatAmount(row.buyingPrice, "0"),
    sortable: false,
    tableColumn: true,
    filterName: "buyingPriceFilter",
    type: "number",
    minWidth: "150px",
  },
  {
    name: "Quotation Date",
    selector: (row) => row.quotationDate,
    sortable: false,
    tableColumn: true,
    filterName: "quotationDateFilter",
    type: "date",
    minWidth: "150px",
  },
  {
    name: "Loading Point",
    selector: (row) => row.loadingPoint,
    cell: (row) => (
      <span className="quotation_location_cell">
        <GoLocation />
        <span title={row.loadingPoint || "-"}>{row.loadingPoint || "-"}</span>
      </span>
    ),
    sortable: false,
    tableColumn: true,
    filterName: "loadingPointFilter",
    type: "text",
    minWidth: "220px",
  },
];

const LoadQuotationsTable = ({ data, selectedMap, onToggleRow, partyHeading }) => {
  const rows = safeArray(data?.loadQuotations);
  if (!rows.length) return null;

  const currentSelection = selectedMap?.[data?.id] || {};

  return (
    <div className="quotation_expand_block" onClick={(event) => event.stopPropagation()}>
      <h5>{partyHeading} Load Quotations</h5>
      <div className="quotation_subtable_wrap">
        <table className="quotation_subtable">
          <thead>
            <tr>
              <th />
              <th>Load ID</th>
              <th>{partyHeading}</th>
              <th>POC Name</th>
              <th>POC Contact</th>
              <th>Buying Price (PKR)</th>
              <th>Quotation Date</th>
              <th>Loading Point</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isSelected = Boolean(currentSelection[row.id]);

              return (
                <tr key={row.id} className={isSelected ? "is-selected" : ""}>
                  <td>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(event) =>
                        onToggleRow(data.id, row.id, event.target.checked)
                      }
                    />
                  </td>
                  <td>{row.id || "-"}</td>
                  <td className="quotation_link">{row.partyName || "-"}</td>
                  <td className="quotation_link">{row.pocName || "-"}</td>
                  <td>{row.pocContact || "-"}</td>
                  <td>{formatAmount(row.buyingPrice, "0")}</td>
                  <td>{row.quotationDate || "-"}</td>
                  <td>
                    <span className="quotation_location_cell">
                      <GoLocation />
                      <span title={row.loadingPoint || "-"}>{row.loadingPoint || "-"}</span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Quotations = () => {
  const dispatch = useDispatch();
  const [recordsByTab, setRecordsByTab] = useState(() => loadQuotationRecords());
  const [activeTab, setActiveTab] = useState(QUOTATION_TAB.customer);
  const [tableState, setTableState] = useState({
    [QUOTATION_TAB.customer]: createTableState(),
    [QUOTATION_TAB.transporter]: createTableState(),
  });
  const [selectedLoadQuotations, setSelectedLoadQuotations] = useState({});
  const [modalState, setModalState] = useState({
    open: false,
    mode: QUOTATION_TAB.customer,
  });
  const [formValues, setFormValues] = useState({
    loadId: "",
    partyName: "",
    buyingPrice: "",
    quotationDate: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const loadBookingRows = useMemo(() => loadBookings(), []);
  const activeTabMeta = QUOTATION_TAB_META[activeTab];

  useEffect(() => {
    dispatch(fetchNotificationsData());
  }, [dispatch]);

  useEffect(() => {
    saveQuotationRecords(recordsByTab);
  }, [recordsByTab]);

  useEffect(() => {
    setSelectedLoadQuotations((previous) => {
      const next = { ...previous };
      const allRows = [
        ...safeArray(recordsByTab?.[QUOTATION_TAB.customer]),
        ...safeArray(recordsByTab?.[QUOTATION_TAB.transporter]),
      ];

      allRows.forEach((row) => {
        const nestedRows = safeArray(row?.loadQuotations);
        if (!nestedRows.length) return;

        const rowSelection = { ...(next[row.id] || {}) };
        const availableIds = new Set(nestedRows.map((item) => item.id));

        Object.keys(rowSelection).forEach((id) => {
          if (!availableIds.has(id)) delete rowSelection[id];
        });

        if (!Object.keys(rowSelection).length && nestedRows[0]?.id) {
          rowSelection[nestedRows[0].id] = true;
        }

        next[row.id] = rowSelection;
      });

      Object.keys(next).forEach((rowId) => {
        if (!allRows.some((row) => row.id === rowId)) {
          delete next[rowId];
        }
      });

      return next;
    });
  }, [recordsByTab]);

  const currentTableState = tableState[activeTab] || createTableState();
  const currentRows = safeArray(recordsByTab?.[activeTab]);

  const searchedRows = useMemo(
    () => searchQuotationRows(currentRows, currentTableState.searchText),
    [currentRows, currentTableState.searchText]
  );

  const filteredRows = useMemo(
    () => applyQuotationFilters(searchedRows, currentTableState.searchFilters),
    [searchedRows, currentTableState.searchFilters]
  );

  const totalItems = filteredRows.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalItems / Math.max(1, Number(currentTableState.limit) || 1))
  );

  const rows = useMemo(
    () =>
      paginateRows(
        filteredRows,
        currentTableState.currentPage,
        currentTableState.limit
      ),
    [filteredRows, currentTableState.currentPage, currentTableState.limit]
  );

  const customerColumns = useMemo(
    () => buildQuotationColumns(QUOTATION_TAB_META[QUOTATION_TAB.customer].partyLabel),
    []
  );
  const transporterColumns = useMemo(
    () => buildQuotationColumns(QUOTATION_TAB_META[QUOTATION_TAB.transporter].partyLabel),
    []
  );

  const updateActiveTableState = useCallback(
    (patch) => {
      setTableState((previous) => ({
        ...previous,
        [activeTab]: {
          ...previous[activeTab],
          ...patch,
        },
      }));
    },
    [activeTab]
  );

  useEffect(() => {
    if (currentTableState.currentPage <= totalPages) return;
    updateActiveTableState({ currentPage: totalPages });
  }, [currentTableState.currentPage, totalPages, updateActiveTableState]);

  const loadBookingOptions = useMemo(() => {
    const map = new Map();

    safeArray(loadBookingRows).forEach((item) => {
      const loadId = String(item?.id || "").trim();
      if (!loadId || map.has(loadId)) return;

      map.set(loadId, {
        label: loadId,
        value: loadId,
        loadingPoint: item?.loadingPoint || "-",
        customerName: item?.customerName || "",
        transporterName: item?.transporterName || "",
      });
    });

    return Array.from(map.values());
  }, [loadBookingRows]);

  const modalPartyOptions = useMemo(() => {
    const mode = modalState.mode;
    const map = new Map();

    safeArray(recordsByTab?.[mode]).forEach((row) => {
      const name = String(row?.partyName || "").trim();
      if (!name || map.has(normalize(name))) return;

      map.set(normalize(name), {
        label: name,
        value: name,
        pocName: row?.pocName || "",
        pocContact: row?.pocContact || "",
      });
    });

    safeArray(loadBookingOptions).forEach((option) => {
      const fallbackName =
        mode === QUOTATION_TAB.customer
          ? option?.customerName
          : option?.transporterName;
      const name = String(fallbackName || "").trim();
      if (!name || map.has(normalize(name))) return;

      map.set(normalize(name), {
        label: name,
        value: name,
        pocName: "",
        pocContact: "",
      });
    });

    return Array.from(map.values());
  }, [loadBookingOptions, modalState.mode, recordsByTab]);

  const selectedLoadBooking = useMemo(
    () => loadBookingOptions.find((option) => option.value === formValues.loadId) || null,
    [formValues.loadId, loadBookingOptions]
  );

  const selectedParty = useMemo(
    () =>
      modalPartyOptions.find((option) => option.value === formValues.partyName) ||
      null,
    [formValues.partyName, modalPartyOptions]
  );

  const setCurrentPage = useCallback(
    (page) => {
      updateActiveTableState({ currentPage: page });
    },
    [updateActiveTableState]
  );

  const setLimit = useCallback(
    (limit) => {
      updateActiveTableState({ limit, currentPage: 1 });
    },
    [updateActiveTableState]
  );

  const setSearchText = useCallback(
    (searchText) => {
      updateActiveTableState({ searchText, currentPage: 1 });
    },
    [updateActiveTableState]
  );

  const setSearchFilters = useCallback(
    (valueOrFactory) => {
      setTableState((previous) => {
        const previousFilters = previous?.[activeTab]?.searchFilters || {};
        const nextFilters =
          typeof valueOrFactory === "function"
            ? valueOrFactory(previousFilters)
            : valueOrFactory;

        return {
          ...previous,
          [activeTab]: {
            ...previous[activeTab],
            searchFilters: nextFilters || {},
            currentPage: 1,
          },
        };
      });
    },
    [activeTab]
  );

  const handleToggleLoadQuotation = useCallback((parentId, rowId, checked) => {
    setSelectedLoadQuotations((previous) => {
      const parentSelection = { ...(previous[parentId] || {}) };
      if (checked) parentSelection[rowId] = true;
      else delete parentSelection[rowId];

      return {
        ...previous,
        [parentId]: parentSelection,
      };
    });
  }, []);

  const resetModal = useCallback(() => {
    setFormValues({
      loadId: "",
      partyName: "",
      buyingPrice: "",
      quotationDate: "",
    });
    setFormErrors({});
  }, []);

  const openCreateModal = useCallback(
    (mode) => {
      resetModal();
      setModalState({ open: true, mode });
    },
    [resetModal]
  );

  const closeCreateModal = useCallback(() => {
    setModalState((previous) => ({ ...previous, open: false }));
    resetModal();
  }, [resetModal]);

  const setFormField = useCallback((field, value) => {
    setFormValues((previous) => ({
      ...previous,
      [field]: value,
    }));
    setFormErrors((previous) => ({
      ...previous,
      [field]: "",
    }));
  }, []);

  const handleCreateQuotation = useCallback(() => {
    const nextErrors = {};
    const buyingPrice = Number(formValues.buyingPrice);

    if (!formValues.loadId) nextErrors.loadId = "Load Booking ID is required";
    if (!formValues.partyName) nextErrors.partyName = "Selection is required";
    if (!Number.isFinite(buyingPrice) || buyingPrice <= 0) {
      nextErrors.buyingPrice = "Buying Price must be greater than zero";
    }
    if (!formValues.quotationDate) {
      nextErrors.quotationDate = "Date of Quotation is required";
    }

    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const mode = modalState.mode;
    const currentRowsByMode = safeArray(recordsByTab?.[mode]);
    const generatedId = generateQuotationId(mode, currentRowsByMode);
    const loadingPoint = selectedLoadBooking?.loadingPoint || "-";
    const nestedRows = buildLoadQuotationRows(
      formValues.loadId,
      buyingPrice,
      formValues.quotationDate,
      loadingPoint
    );

    const nextRecord = {
      id: generatedId,
      loadId: formValues.loadId,
      partyName: formValues.partyName,
      pocName: selectedParty?.pocName || "POC Name",
      pocContact: selectedParty?.pocContact || "-",
      buyingPrice,
      quotationDate: formValues.quotationDate,
      loadingPoint,
      loadQuotations: nestedRows,
    };

    setRecordsByTab((previous) => ({
      ...previous,
      [mode]: [nextRecord, ...safeArray(previous?.[mode])],
    }));

    setTableState((previous) => ({
      ...previous,
      [mode]: {
        ...previous[mode],
        currentPage: 1,
      },
    }));

    if (nestedRows[0]?.id) {
      setSelectedLoadQuotations((previous) => ({
        ...previous,
        [nextRecord.id]: {
          [nestedRows[0].id]: true,
        },
      }));
    }

    setActiveTab(mode);
    closeCreateModal();
  }, [
    closeCreateModal,
    formValues.buyingPrice,
    formValues.loadId,
    formValues.partyName,
    formValues.quotationDate,
    modalState.mode,
    recordsByTab,
    selectedLoadBooking,
    selectedParty,
  ]);

  const activeOverview = useMemo(
    () => buildQuotationOverview(currentRows),
    [currentRows]
  );
  const latestDateLabel = activeOverview.latestDate
    ? toDateLabel(activeOverview.latestDate)
    : "--";

  const overviewCards = useMemo(() => {
    const isCustomerTab = activeTab === QUOTATION_TAB.customer;
    const entityLabel = isCustomerTab ? "Customer" : "Transporter";

    return [
      {
        title: `Total ${entityLabel} Quotations`,
        value: activeOverview.total,
        text: `${activeOverview.loadCount} unique load IDs covered`,
        icon: <BsCheckCircle />,
      },
      {
        title: `${entityLabel} Quote Value`,
        value: formatAmount(activeOverview.totalAmount, "0"),
        text: `Combined quoted amount in ${activeOverview.total} records`,
        icon: <BsTruck />,
        className: "completed",
      },
      {
        title: `Highest ${entityLabel} Quote`,
        value: formatAmount(activeOverview.highest, "0"),
        text: "Highest quoted price in this tab",
        icon: <BsXOctagon />,
        className: "late",
      },
      {
        title: `Average ${entityLabel} Quote`,
        value: formatAmount(activeOverview.average, "0"),
        text: `Last updated : ${latestDateLabel}`,
        icon: <FiCalendar />,
        className: "pending-delivery",
      },
    ];
  }, [activeOverview, activeTab, latestDateLabel]);

  const tabs = useMemo(
    () => [
      {
        key: QUOTATION_TAB.customer,
        label: QUOTATION_TAB_META[QUOTATION_TAB.customer].title,
        icon: QUOTATION_TAB_META[QUOTATION_TAB.customer].icon,
      },
      {
        key: QUOTATION_TAB.transporter,
        label: QUOTATION_TAB_META[QUOTATION_TAB.transporter].title,
        icon: QUOTATION_TAB_META[QUOTATION_TAB.transporter].icon,
      },
    ],
    []
  );

  const renderNoData = (tab) => {
    const tabMeta = QUOTATION_TAB_META[tab];

    return (
      <div className="add_trip_suggestion_div quotation_empty_state">
        <PiTableBold />
        <h4>{tabMeta.emptyTitle}</h4>
        <p>{tabMeta.emptyDescription}</p>
        <button
          type="button"
          className="main_btn quotation_empty_btn"
          onClick={() => openCreateModal(tab)}
        >
          <FiPlus />
          {tabMeta.createLabel}
        </button>
      </div>
    );
  };

  return (
    <div>
      <Breadcrumbs values={["Quotations", activeTabMeta.title]} />

      <div className="access_view">
        <h2>Quotations</h2>

        <div className="quotation_header_actions">
          <Notification />

          <button
            type="button"
            className="quotation_create_btn"
            onClick={() => openCreateModal(activeTab)}
          >
            <FiPlus />
            {activeTabMeta.createLabel}
          </button>
        </div>
      </div>

      <div className="px-4 quotation_tabs_wrap">
        <CustomTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      <div className="quotation_stats_wrapper">
        <Stats statsData={overviewCards} />
      </div>

      {activeTab === QUOTATION_TAB.customer && (
        <div className="procurement_table_box quotations_table_box">
          <NewTableComponent
            columns={customerColumns}
            data={rows}
            currentPage={currentTableState.currentPage}
            setCurrentPage={setCurrentPage}
            limit={currentTableState.limit}
            setLimit={setLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            onSearch={setSearchText}
            searchValue={currentTableState.searchText}
            setSearchFilters={setSearchFilters}
            searchFilters={currentTableState.searchFilters}
            loading={false}
            paginationLoading={false}
            limitOptions={QUOTATION_LIMIT_OPTIONS}
            tableClassName="transporters_table quotations_table"
            expandableRows={true}
            expandableRowsComponent={LoadQuotationsTable}
            expandableRowsComponentProps={{
              selectedMap: selectedLoadQuotations,
              onToggleRow: handleToggleLoadQuotation,
              partyHeading: QUOTATION_TAB_META[QUOTATION_TAB.customer].partyLabel,
            }}
            expandableRowDisabled={(row) => !safeArray(row?.loadQuotations).length}
            expandableIcon={{
              collapsed: <IoMdArrowDropright />,
              expanded: <IoMdArrowDropright style={{ transform: "rotate(90deg)" }} />,
            }}
            noDataComponent={renderNoData(QUOTATION_TAB.customer)}
          />
        </div>
      )}

      {activeTab === QUOTATION_TAB.transporter && (
        <div className="procurement_table_box quotations_table_box">
          <NewTableComponent
            columns={transporterColumns}
            data={rows}
            currentPage={currentTableState.currentPage}
            setCurrentPage={setCurrentPage}
            limit={currentTableState.limit}
            setLimit={setLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            onSearch={setSearchText}
            searchValue={currentTableState.searchText}
            setSearchFilters={setSearchFilters}
            searchFilters={currentTableState.searchFilters}
            loading={false}
            paginationLoading={false}
            limitOptions={QUOTATION_LIMIT_OPTIONS}
            tableClassName="transporters_table quotations_table"
            expandableRows={true}
            expandableRowsComponent={LoadQuotationsTable}
            expandableRowsComponentProps={{
              selectedMap: selectedLoadQuotations,
              onToggleRow: handleToggleLoadQuotation,
              partyHeading: QUOTATION_TAB_META[QUOTATION_TAB.transporter].partyLabel,
            }}
            expandableRowDisabled={(row) => !safeArray(row?.loadQuotations).length}
            expandableIcon={{
              collapsed: <IoMdArrowDropright />,
              expanded: <IoMdArrowDropright style={{ transform: "rotate(90deg)" }} />,
            }}
            noDataComponent={renderNoData(QUOTATION_TAB.transporter)}
          />
        </div>
      )}

      <CustomModal
        show={modalState.open}
        handleClose={closeCreateModal}
        heading={QUOTATION_TAB_META[modalState.mode].modalTitle}
        size="lg"
        dialogClassName="quotation_modal_dialog"
      >
        <div className="quotation_modal_body">
          <Select
            label="Load Booking ID *"
            placeholder="Select Load Booking ID"
            options={loadBookingOptions}
            value={loadBookingOptions.find((option) => option.value === formValues.loadId)}
            onChange={(option) => setFormField("loadId", option ? option.value : "")}
            error={formErrors.loadId}
            touched={Boolean(formErrors.loadId)}
          />

          <Select
            label={`${QUOTATION_TAB_META[modalState.mode].partyLabel} *`}
            placeholder={QUOTATION_TAB_META[modalState.mode].partyPlaceholder}
            options={modalPartyOptions}
            value={modalPartyOptions.find(
              (option) => option.value === formValues.partyName
            )}
            onChange={(option) => setFormField("partyName", option ? option.value : "")}
            error={formErrors.partyName}
            touched={Boolean(formErrors.partyName)}
          />

          <div className="quotation_modal_row">
            <Input
              label="Buying Price (PKR)"
              placeholder="00"
              name="buyingPrice"
              value={formValues.buyingPrice}
              onChange={(event) =>
                setFormField(
                  "buyingPrice",
                  String(event.target.value || "").replace(/[^\d.]/g, "")
                )
              }
              onBlur={() => {}}
              error={formErrors.buyingPrice}
              touched={Boolean(formErrors.buyingPrice)}
            />

            <Input
              label="Date of Quotation"
              placeholder="YY-DD-MM"
              type="date"
              name="quotationDate"
              value={formValues.quotationDate}
              onChange={(event) => setFormField("quotationDate", event.target.value)}
              onBlur={() => {}}
              error={formErrors.quotationDate}
              touched={Boolean(formErrors.quotationDate)}
            />
          </div>

          <div className="quotation_modal_actions">
            <button type="button" className="cancel_btn" onClick={closeCreateModal}>
              Cancel
            </button>
            <button
              type="button"
              className="main_btn"
              onClick={handleCreateQuotation}
              disabled={
                !formValues.loadId ||
                !formValues.partyName ||
                !formValues.buyingPrice ||
                !formValues.quotationDate
              }
            >
              {QUOTATION_TAB_META[modalState.mode].createLabel}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default Quotations;
